import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import styles from './Bouton.module.css'

export type VarianteBouton = 'primaire' | 'secondaire' | 'discret' | 'destructeur' | 'avertissement'

interface ProprietesBouton extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  readonly variante?: VarianteBouton
  readonly pleineLargeur?: boolean
  /**
   * Désactive le bouton ET signale l'attente. Un bouton qui reste cliquable
   * pendant une mutation produit des doubles soumissions — sur une connexion
   * lente d'agence, ce n'est pas un cas d'école.
   */
  readonly enCours?: boolean
  /** Bouton carré pour une icône seule (▲ ▼) : `aria-label` obligatoire alors. */
  readonly carre?: boolean
  /** React 19 : la ref passe comme une prop, pour rendre le focus après une action. */
  readonly ref?: Ref<HTMLButtonElement>
  readonly children: ReactNode
}

export function Bouton({
  variante = 'primaire',
  pleineLargeur = false,
  enCours = false,
  carre = false,
  disabled,
  type = 'button',
  children,
  ...reste
}: ProprietesBouton): React.JSX.Element {
  const classes = [
    styles.bouton,
    styles[variante],
    pleineLargeur ? styles.pleineLargeur : '',
    carre ? styles.carre : '',
    enCours ? styles.enCours : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      {...reste}
      type={type}
      className={classes}
      disabled={disabled === true || enCours}
      aria-busy={enCours || undefined}
    >
      {children}
    </button>
  )
}

interface ProprietesLienBouton extends Omit<LinkProps, 'className'> {
  readonly variante?: VarianteBouton
  readonly children: ReactNode
}

/**
 * Un lien à l'allure d'un bouton — mêmes variantes. Pour une action qui MÈNE
 * ailleurs (« Voir les salariés ») : un vrai lien s'ouvre dans un nouvel
 * onglet, au clic du milieu comme au clavier.
 */
export function LienBouton({
  variante = 'primaire',
  children,
  ...reste
}: ProprietesLienBouton): React.JSX.Element {
  return (
    <Link {...reste} className={[styles.bouton, styles[variante], styles.lien].join(' ')}>
      {children}
    </Link>
  )
}
